import { useContext, useEffect, useState, useRef, useCallback } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { uploadSVGToStorage, uploadReferenceArtworkToStorage } from "../../lib/firebase/firebase";
import { trackIntercomEvent } from "../../lib/intercom/intercom";
import { isNativeiOS } from "../../lib/utils/platform";

import "antd/dist/antd.css";
import "./index.css";

import AutoUploadImage from "./components/AutoUploadImage";
// import UploadImage64 from './components/UploadImage64';
import AppContext from "../../contexts/AppContext";
import { useProcessedImage } from "../../contexts/ProcessedImageContext";
import { useAuth } from "../../contexts/AuthContext";

import { writeSVGToDatabase } from "../../lib/firebase/firebase";
import { SVG_FOLDER, SVG_GENERATED_FOLDER } from "../../config/firebaseVariant";
import { isEmbroideryTrace } from "../../config/appVariant";

import { useDispatch } from "react-redux";
import {
  addNewlyGeneratedFile,
  setShowEditor,
} from "../../redux/slices/MyFiles";
import { addNewlyUploadedFile } from "../../redux/slices/UploadedFiles";
import ConverterDesktop from "./components/ConverterDesktop";
import ConverterMobile from "./components/ConverterMobile";
import { createReferenceArtworkRegistration } from "../SvgEditor/helpers/referenceArtworkRegistration";

const Converter = (props) => {
  const history = useHistory();
  const location = useLocation();
  const { user } = useAuth();
  const { setSvgStore, image64, setImage64, referenceImage64, setReferenceImage64, imageSource, setImageSource, imageSEO, fileName, guestMode, onPaywallTrigger, pendingEmbroideryMode, setPendingEmbroideryMode } =
    useContext(AppContext);
  const { imagePrompt } = useContext(AppContext);
  
  // Get processed image context (replaces window globals)
  const { 
    guestProcessedImage, 
    tutorialProcessedImage, 
    clearGuestProcessedImage, 
    clearTutorialProcessedImage,
    hasPendingProcessedImage 
  } = useProcessedImage();
  
  // Props can override context for guest mode
  const isGuestMode = props.guestMode || guestMode || false;
  const handlePaywallTrigger = props.onPaywallTrigger || onPaywallTrigger;
  const pendingFileId = props.pendingFileId || null;
  
  // AI analysis result for smart retrace triggering
  const aiAnalysisResult = props.aiAnalysisResult || null;
  
  // AI analysis result passed to children
  const [localAiAnalysisResult, setLocalAiAnalysisResult] = useState(null);
  const effectiveAiAnalysisResult = aiAnalysisResult || localAiAnalysisResult;
  
  // Check if we're on the /convert page (GuestConverter)
  const isOnConvertPage = location.pathname === "/convert";
  const [file, setFile] = useState("");
  const [, setFileLocation] = useState("");
  const [originalDimensions, setOriginalDimensions] = useState(undefined);
  const [colors, setColors] = useState([]);
  const [clickedColor, setClickedColor] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [, setIsModalVisible] = useState(false);
  const [isCropVisible, setIsCropVisible] = useState(false);
  const [downloadSVGLink, setDownloadSVGLink] = useState("");
  const [, setShareLink] = useState("");
  const [smoothness, setSmoothness] = useState(1);
  const [despeckle, setDespeckle] = useState(2);
  const [simplifyCurves, setSimplifyCurves] = useState(0.2);
  const [useMorphology, setUseMorphology] = useState(false);
  const [imageDimensions, setImageDimensions] = useState([0, 0]);
  const [scale, setScale] = useState(1);
  const [largeImageWarning, setLargeImageWarning] = useState(false);
  const [wasUpscaled, setWasUpscaled] = useState(false);
  const [upscaleReason, setUpscaleReason] = useState("");
  const [svg, setSvg] = useState();
  const [, setPreviewReady] = useState(false);
  const [hasAutoSavedGuestSVG, setHasAutoSavedGuestSVG] = useState(false);

  const [svgPreview, setSvgPreview] = useState();

  // const [showPreviewScreen, setShowPreviewScreen] = useState(false);

  const [canvasHeight, setCanvasHeight] = useState(0);
  const [ai, setAi] = useState(true);

  const dispatch = useDispatch();

  const artworkUploadRef = useRef(null);
  const referenceArtworkDataRef = useRef(null);
  const referenceArtworkDimensionsRef = useRef(null);
  if (isEmbroideryTrace && !referenceArtworkDataRef.current && (referenceImage64 || image64)) {
    referenceArtworkDataRef.current = referenceImage64 || image64;
  }
  if (
    isEmbroideryTrace &&
    !referenceArtworkDimensionsRef.current &&
    Number(imageDimensions?.[0]) > 0 &&
    Number(imageDimensions?.[1]) > 0
  ) {
    referenceArtworkDimensionsRef.current = [Number(imageDimensions[0]), Number(imageDimensions[1])];
  }

  const [crop, setCrop] = useState({
    unit: "px", // Can be 'px' or '%'
    x: null,
    y: null,
    width: null,
    height: null,
  });

  const [screenSize, getDimension] = useState({
    dynamicWidth: window.innerWidth,
    dynamicHeight: window.innerHeight,
  });

  const setDimension = () => {
    getDimension({
      dynamicWidth: window.innerWidth,
      dynamicHeight: window.innerHeight,
    });
  };

  const clearFields = () => {
    setSvgPreview(undefined);
    setFile("");
    setFileLocation("");
    setColors([]);
    setImage64(undefined);
    if (setReferenceImage64) setReferenceImage64(undefined);
    setImageSource(undefined);
    // Clear AI analysis result for next conversion
    setLocalAiAnalysisResult(null);
    // Reset other converter state
    setWasUpscaled(false);
    setUpscaleReason("");
    setOriginalDimensions(undefined);
    setLargeImageWarning(false);
    setImageDimensions([0, 0]);
    setCrop({ unit: "px", x: null, y: null, width: null, height: null });
    artworkUploadRef.current = null;
    referenceArtworkDataRef.current = null;
    referenceArtworkDimensionsRef.current = null;
    props.onFlowClose?.();
  };

  // The latest successful AI retrace becomes the reference for this vector.
  // Replace its dimensions and invalidate any upload of the previous bitmap.
  const handleRetracedArtwork = useCallback((dataUrl, dimensions) => {
    if (!isEmbroideryTrace || !dataUrl) return;
    referenceArtworkDataRef.current = dataUrl;
    referenceArtworkDimensionsRef.current = dimensions;
    artworkUploadRef.current = null;
    setReferenceImage64?.(dataUrl);
  }, [setReferenceImage64]);

  const getArtworkRegistration = useCallback(() => createReferenceArtworkRegistration(
    referenceArtworkDimensionsRef.current,
    imageDimensions,
    crop,
  ), [imageDimensions, crop]);

  const buildReferenceArtwork = useCallback(async () => {
    const sourceArtwork = referenceArtworkDataRef.current || referenceImage64 || image64;
    if (!isEmbroideryTrace || !sourceArtwork) return null;
    if (!artworkUploadRef.current) {
      artworkUploadRef.current = uploadReferenceArtworkToStorage(sourceArtwork).catch((error) => {
        artworkUploadRef.current = null;
        throw error;
      });
    }
    const uploaded = await artworkUploadRef.current;
    return {
      path: uploaded.res.ref.fullPath,
      url: uploaded.url,
      mimeType: uploaded.mimeType,
      name: fileName || "Source artwork",
      ...getArtworkRegistration(),
    };
  }, [referenceImage64, image64, fileName, getArtworkRegistration]);

  useEffect(() => {
    if (document.getElementById("middleBox") !== null) {
      const element = document.getElementById("middleBox");
      const height = element.getBoundingClientRect().height;
      setCanvasHeight(height);
    }
  }, []);

  useEffect(() => {
    if (file !== "") {
      // File is set — clear the processing overlay immediately
      if (props.setProcessingStep) {
        props.setProcessingStep(null);
      }
      setTimeout(() => {
        props.setConverterOpen(true);
      }, 1000);
    } else {
      if (!isGuestMode && !hasPendingProcessedImage()) {
        props.setConverterOpen(false);
      }
      setPreviewReady(false);
    }
  }, [file, isGuestMode, hasPendingProcessedImage]);

  // Check for tutorial processed image when converter opens
  useEffect(() => {
    if (props.converterOpen && tutorialProcessedImage && file === "") {
      console.log('🎯 Converter: Found tutorial processed image, applying...');
      const result = tutorialProcessedImage;
      
      // Apply the processed image data to converter state
      setFile(result.file);
      setOriginalDimensions(result.originalDimensions);
      setLargeImageWarning(result.largeImageWarning);
      setCrop(result.crop);
      setImageDimensions(result.imageDimensions);
      setDespeckle(result.despeckle);
      setColors(result.colors);
      
      // Also set wasUpscaled for auto-retrace feature (from claimed files)
      if (result.wasUpscaled !== undefined) {
        setWasUpscaled(result.wasUpscaled);
      }
      if (result.upscaleReason !== undefined) {
        setUpscaleReason(result.upscaleReason);
      }
      
      // Clear the context after use (prevents memory leaks)
      clearTutorialProcessedImage();
      console.log('✅ Converter: Tutorial image data applied, wasUpscaled:', result.wasUpscaled);
    }
  }, [props.converterOpen, tutorialProcessedImage, clearTutorialProcessedImage]);
  
  // Check for guest converter processed image
  useEffect(() => {
    if (props.converterOpen && guestProcessedImage && file === "") {
      console.log('🎯 Converter: Found guest converter processed image, applying...');
      const result = guestProcessedImage;
      
      // Apply the processed image data to converter state
      setFile(result.file);
      setOriginalDimensions(result.originalDimensions);
      setLargeImageWarning(result.largeImageWarning);
      setCrop(result.crop);
      setImageDimensions(result.imageDimensions);
      setDespeckle(result.despeckle);
      setColors(result.colors);
      
      // Check AI analysis result - if needsRetrace is true, trigger auto-retrace
      // This takes priority over the dimension-based detection
      if (effectiveAiAnalysisResult?.needsRetrace) {
        console.log('🤖 AI Analysis says retrace needed:', effectiveAiAnalysisResult.reason);
        setWasUpscaled(true); // This triggers auto-retrace in AIToolkitBar
        setUpscaleReason(effectiveAiAnalysisResult.hasGradients ? 'gradients' : 'ai-analysis');
      } else if (result.wasUpscaled !== undefined) {
        setWasUpscaled(result.wasUpscaled);
      }
      
      if (result.upscaleReason !== undefined && !effectiveAiAnalysisResult?.needsRetrace) {
        setUpscaleReason(result.upscaleReason);
      }
      
      // Clear the context after use (prevents memory leaks)
      clearGuestProcessedImage();
      console.log('✅ Converter: Guest converter image data applied, AI retrace needed:', effectiveAiAnalysisResult?.needsRetrace);
    }
  }, [props.converterOpen, guestProcessedImage, clearGuestProcessedImage, effectiveAiAnalysisResult]);

  // Auto-save guest-generated SVGs as public when preview is ready
  useEffect(() => {
    const autoSaveGuestSVG = async () => {
      // Get pending file ID from URL to check if this came from generate flow
      const urlParams = new URLSearchParams(window.location.search);
      const fileId = urlParams.get("file");
      
      // Check if this is a generated image:
      // 1. imageSource is explicitly "generated", OR
      // 2. We have a fileId in URL (indicates it came from generate flow), OR
      // 3. We have an imagePrompt (indicates it was generated)
      const isGeneratedImage = imageSource === "generated" || 
                               (fileId && isOnConvertPage) || 
                               !!imagePrompt;

      // Debug logging
      console.log("🔍 Auto-save check:", {
        isGuestMode,
        hasSvg: !!svg,
        hasAutoSavedGuestSVG,
        imageSource,
        isOnConvertPage,
        fileId,
        hasImagePrompt: !!imagePrompt,
        isGeneratedImage,
        allConditionsMet: isGuestMode && svg && !hasAutoSavedGuestSVG && isGeneratedImage && isOnConvertPage
      });

      // Only save if:
      // 1. We're in guest mode
      // 2. SVG is ready
      // 3. We haven't already saved this SVG
      // 4. Image is generated (from AI generation)
      // 5. We're on the convert page
      if (
        isGuestMode &&
        svg &&
        !hasAutoSavedGuestSVG &&
        isGeneratedImage &&
        isOnConvertPage
      ) {
        try {
          console.log("🎨 Auto-saving guest-generated SVG as public...");
          setHasAutoSavedGuestSVG(true);

          // Prepare headers with auth token if user is logged in
          const headers = {
            "Content-Type": "application/json",
          };
          
          // Add auth token if user is logged in
          if (user) {
            try {
              const accessToken = await user.getIdToken();
              headers.Authorization = `Bearer ${accessToken}`;
            } catch (tokenError) {
              console.warn("Failed to get auth token, saving as guest:", tokenError);
            }
          }

          // Call Cloud Function to save as public
          const response = await fetch(
            "https://us-central1-svgtrace-pro.cloudfunctions.net/saveGuestSVGAsPublic",
            {
              method: "POST",
              headers: headers,
              body: JSON.stringify({
                svg: svg,
                prompt: imagePrompt || "",
                seoTitle: imageSEO?.title || imagePrompt || "",
                seoDescription: imageSEO?.description || imagePrompt || "",
                pendingFileId: fileId,
              }),
            }
          );

          if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.error || "Failed to save SVG");
          }

          const result = await response.json();
          console.log("✅ Guest SVG saved as public:", result);
        } catch (error) {
          console.error("❌ Error auto-saving guest SVG:", error);
          // Don't show error to user - this is a background operation
        }
      }
    };

    autoSaveGuestSVG();
  }, [svg, isGuestMode, hasAutoSavedGuestSVG, imageSource, imagePrompt, imageSEO, isOnConvertPage, user]);

  useEffect(() => {
    window.addEventListener("resize", setDimension);

    return () => {
      window.removeEventListener("resize", setDimension);
    };
  }, [screenSize]);

  useEffect(() => {
    if (clickedColor !== "") {
      setColors([...colors, [clickedColor, true]]);
      setClickedColor("");
    }
  }, [clickedColor]);

  // Persists the DST file + AI analysis to Firebase in the background.
  // Runs fire-and-forget so it never blocks navigation to the editor.
  async function saveToCloud(openEditor, sharePublicly = null) {
    // Gate behind paywall in guest mode
    if (isGuestMode) {
      console.log("🔒 Guest mode: Triggering paywall for save action");
      if (handlePaywallTrigger) {
        handlePaywallTrigger(openEditor ? "editor" : "save", svg);
      }
      return;
    }
    
    if (imageSource === undefined) {
      throw new Error(
        "Image source is undefined. Cannot determine the folder name for saving."
      );
    }

    // Retracing from inside the Stitch Editor replaces the current working
    // vector instead of creating a second design or opening another editor.
    if (props.onRetraceComplete) {
      const artwork = await buildReferenceArtwork();
      await props.onRetraceComplete(svg, getArtworkRegistration(), artwork);
      clearFields();
      props.setConverterOpen(false);
      return;
    }

    const base64Data = btoa(svg);
    // Determine folder based on source:
    // - "svg_generated" for AI-generated images (from text prompts)
    // - "svg" for everything else (uploaded, sharpened, and future modifications)
    let folderName = imageSource === "generated" ? SVG_GENERATED_FOLDER : SVG_FOLDER;

    const dataUrl = `data:image/svg+xml;base64,${base64Data}`;

    // On iOS, open the editor immediately with local SVG data —
    // Firebase Storage uploads hang indefinitely in the WKWebView.
    // The upload then runs in the background; editor still works without it.
    if (isNativeiOS() && openEditor) {
      const iosWorkflow = pendingEmbroideryMode ? "stitch" : "vector";
      const localArtworkData = referenceArtworkDataRef.current || referenceImage64 || image64;
      const localReferenceArtwork = isEmbroideryTrace && localArtworkData ? {
        dataUrl: localArtworkData,
        name: fileName || "Source artwork",
        ...getArtworkRegistration(),
      } : null;
      const localFile = {
        id: `local_${Date.now()}`,
        name: imageSource === "modified" ? imagePrompt : `converted_${Date.now()}.svg`,
        uri: null,
        url: null,
        type: imageSource || "uploaded",
        workflow: iosWorkflow,
        ownerUid: user?.uid,
        isPublic: false,
        isLocalOnly: true,
        svgData: svg, // raw SVG string — used by SvgUpdater to skip Firebase fetch
        referenceArtwork: localReferenceArtwork,
      };
      dispatch(setShowEditor({ showEditor: true, currentFile: localFile }));
      setSvgStore({ svg });
      clearFields();
      if (setPendingEmbroideryMode) setPendingEmbroideryMode(false);
      // Fire-and-forget background upload so the file eventually persists
      const fileType = imageSource || "uploaded";
      uploadSVGToStorage(dataUrl, folderName)
        .then(({ res, url }) =>
          writeSVGToDatabase(
            res.ref.fullPath,
            imageSource === "modified" ? imagePrompt : "",
            imageSEO,
            false,
            fileType,
            iosWorkflow
          ).then((docRef) => (docRef ? { docRef, res, url } : null))
        )
        .then((result) => {
          if (result?.docRef && !isOnConvertPage) {
            const newUploaded = {
              id: result.res.ref.name,
              name: imageSource === "modified" ? imagePrompt : result.res.ref.name,
              uri: result.res.ref,
              url: result.url,
              type: fileType,
              workflow: iosWorkflow,
              ownerUid: user?.uid,
              isPublic: false,
              filePath: result.res.ref.fullPath,
              updatedAt: new Date().toISOString(),
            };
            dispatch(addNewlyUploadedFile(newUploaded));
          }
        })
        .catch((err) => console.warn("[iOS] Background upload failed:", err));
      return;
    }

    try {
      let referenceArtwork = null;
      if (isEmbroideryTrace) {
        try {
          referenceArtwork = await buildReferenceArtwork();
        } catch (artworkError) {
          // A reference is useful but must not prevent the traced design from
          // opening. Keep the in-memory copy for this session and report the
          // persistence failure in the console.
          console.warn("[Converter] Reference artwork upload failed:", artworkError);
          const fallbackArtworkData = referenceArtworkDataRef.current || referenceImage64 || image64;
          referenceArtwork = fallbackArtworkData ? {
            dataUrl: fallbackArtworkData,
            name: fileName || "Source artwork",
            ...getArtworkRegistration(),
          } : null;
        }
      }
      const referenceArtworkMetadata = referenceArtwork?.path ? {
        path: referenceArtwork.path,
        mimeType: referenceArtwork.mimeType || null,
        name: referenceArtwork.name || "Source artwork",
        sourceWidth: referenceArtwork.sourceWidth || null,
        sourceHeight: referenceArtwork.sourceHeight || null,
        crop: referenceArtwork.crop || null,
      } : null;
      const response = await uploadSVGToStorage(dataUrl, folderName);
      if (imageSource === "generated") {
        try {
          const filePath = response.res.ref.fullPath;
          console.log(" response.res.", response.res);
          
          // For generated SVGs - default to private for now, will be prompted in editor
          const fileWorkflow = pendingEmbroideryMode ? "stitch" : "vector";
          const docRef = await writeSVGToDatabase(
            filePath,
            imagePrompt,
            imageSEO,
            false, // isPublic = false (will be prompted in editor)
            "generated", // fileType = "generated"
            fileWorkflow,
            referenceArtworkMetadata ? { referenceArtwork: referenceArtworkMetadata } : {}
          );

          if (docRef) {
            trackIntercomEvent('converted_image', { source: 'ai_generated', format: 'svg' });
            console.log("🔥 CONVERTER: NewGenerated file created", response.res);
            const newGenerated = {
              id: response.res.ref.name,
              name: imagePrompt,
              uri: response.res.ref,
              url: response.url,
              type: "generated",
              workflow: fileWorkflow,
              ownerUid: user?.uid, // Include owner for isOwner check in editor
              isPublic: false, // Matches what's written to database
              filePath,
              svgMetadataId: docRef.id,
              referenceArtwork,
            };
            
            // Only add to Redux if NOT coming from guest converter
            // Guest converter files will be fetched from Firebase when Home loads
            if (!isOnConvertPage) {
              console.log("🔥 CONVERTER: Dispatching newGenerated object:", newGenerated);
              console.log("🔥 CONVERTER: File type is:", newGenerated.type);
              dispatch(addNewlyUploadedFile(newGenerated));
            } else {
              console.log("⏭️ CONVERTER: Skipping Redux dispatch - file will be fetched from Firebase");
            }

            // Store saved file info for Home page (useful when redirecting from GuestConverter)
            // Note: Store serializable data only (no Firebase references)
            const savedFileInfo = {
              action: openEditor ? "editor" : "download",
              fileId: newGenerated.id,
              fileName: newGenerated.name,
              filePath: response.res.ref.fullPath,
              url: response.url,
              type: newGenerated.type,
              workflow: fileWorkflow,
              svgMetadataId: docRef.id,
              referenceArtwork: referenceArtwork?.path ? {
                ...referenceArtworkMetadata,
                url: referenceArtwork.url || null,
              } : null,
              embroideryMode: !!pendingEmbroideryMode,
              savedAt: new Date().toISOString(),
            };
            sessionStorage.setItem("savedFile", JSON.stringify(savedFileInfo));
            console.log("💾 CONVERTER: Stored savedFile info for Home page:", savedFileInfo);

            if (openEditor == true) {
              dispatch(setShowEditor({ showEditor: true, currentFile: { ...newGenerated, svgData: svg } }));
              setSvgStore({ firebase: newGenerated.uri });
              if (setPendingEmbroideryMode) setPendingEmbroideryMode(false);
              
              // If on /convert page, redirect to /home to show editor
              if (isOnConvertPage) {
                history.push("/home");
                return;
              }
            }
            
            clearFields();
          }
        } catch (error) {
          console.error("Error writing SVG to Database", error);
        }
      }

      if (imageSource === "uploaded" || imageSource === "sharpened" || imageSource === "modified") {
        try {
          const filePath = response.res.ref.fullPath;
          console.log(" response.res.", response.res);
          // For uploaded/sharpened/modified SVGs (make private)
          const uploadedWorkflow = pendingEmbroideryMode ? "stitch" : "vector";
          const docRef = await writeSVGToDatabase(
            filePath,
            imageSource === "modified" ? imagePrompt : "", // Include prompt for modified images
            imageSEO,
            false, // isPublic = false
            imageSource, // Use actual imageSource: "uploaded", "sharpened", or "modified"
            uploadedWorkflow,
            referenceArtworkMetadata ? { referenceArtwork: referenceArtworkMetadata } : {}
          );

          if (docRef) {
            trackIntercomEvent('converted_image', { source: imageSource, format: 'svg' });
            console.log(`🔥 CONVERTER: New ${imageSource} file created`, response.res);
            const newUploaded = {
              id: response.res.ref.name,
              name: imageSource === "modified" ? imagePrompt : response.res.ref.name,
              uri: response.res.ref,
              url: response.url,
              type: imageSource, // Use actual type: "uploaded", "sharpened", or "modified"
              workflow: uploadedWorkflow,
              ownerUid: user?.uid, // Include owner for isOwner check in editor
              isPublic: false, // Matches what's written to database
              filePath,
              svgMetadataId: docRef.id,
              referenceArtwork,
            };

            // Only add to Redux if NOT coming from guest converter
            // Guest converter files will be fetched from Firebase when Home loads
            if (!isOnConvertPage) {
              console.log(`🔥 CONVERTER: Dispatching new${imageSource} object:`, newUploaded);
              console.log("🔥 CONVERTER: File type is:", newUploaded.type);
              dispatch(addNewlyUploadedFile(newUploaded));
            } else {
              console.log("⏭️ CONVERTER: Skipping Redux dispatch - file will be fetched from Firebase");
            }

            // Store saved file info for Home page (useful when redirecting from GuestConverter)
            // Note: Store serializable data only (no Firebase references)
            const savedFileInfo = {
              action: openEditor ? "editor" : "download",
              fileId: newUploaded.id,
              fileName: newUploaded.name,
              filePath: response.res.ref.fullPath,
              url: response.url,
              type: newUploaded.type,
              workflow: uploadedWorkflow,
              svgMetadataId: docRef.id,
              referenceArtwork: referenceArtwork?.path ? {
                ...referenceArtworkMetadata,
                url: referenceArtwork.url || null,
              } : null,
              embroideryMode: !!pendingEmbroideryMode,
              savedAt: new Date().toISOString(),
            };
            sessionStorage.setItem("savedFile", JSON.stringify(savedFileInfo));
            console.log("💾 CONVERTER: Stored savedFile info for Home page:", savedFileInfo);

            if (openEditor == true) {
              dispatch(setShowEditor({ showEditor: true, currentFile: { ...newUploaded, svgData: svg } }));
              setSvgStore({ firebase: response.res.ref });
              if (setPendingEmbroideryMode) setPendingEmbroideryMode(false);
              
              // If on /convert page, redirect to /home to show editor
              if (isOnConvertPage) {
                history.push("/home");
                return;
              }
            }
            
            clearFields();
          }
        } catch (error) {
          console.error("Error writing SVG to Database", error);
        }
      }
    } catch (error) {
      console.error("An error occurred during upload:", error);
    }
  }

  return (
    <>
      {/* upload button */}
      {file === "" &&
        props.converterOpen === false &&
        svgPreview === undefined && (
          <>
            <AutoUploadImage
              setOriginalDimensions={setOriginalDimensions}
              setCrop={setCrop}
              setLargeImageWarning={setLargeImageWarning}
              setDespeckle={setDespeckle}
              setScale={setScale}
              setImageDimensions={setImageDimensions}
              setFile={setFile}
              setColors={setColors}
              setWasUpscaled={setWasUpscaled}
              setUpscaleReason={setUpscaleReason}
              setLoadingMessage={props.setLoadingMessage}
              setProcessingStep={props.setProcessingStep}
              setAiAnalysisResult={setLocalAiAnalysisResult}
            />
          </>
        )}
      {/* Select Colors Screen */}
      {file !== "" && props.converterOpen && window.innerWidth <= 750 && (
        <ConverterMobile
          onRetracedArtwork={handleRetracedArtwork}
          screenSize={screenSize}
          crop={crop}
          setCrop={setCrop}
          file={file}
          setFile={setFile}
          scale={scale}
          setScale={setScale}
          setClickedColor={setClickedColor}
          setImageDimensions={setImageDimensions}
          originalDimensions={originalDimensions}
          setOriginalDimensions={setOriginalDimensions}
          largeImageWarning={largeImageWarning}
          downloadSVGLink={downloadSVGLink}
          isLoading={isLoading}
          setIsCropVisible={setIsCropVisible}
          isCropVisible={isCropVisible}
          smoothness={smoothness}
          setSmoothness={setSmoothness}
          despeckle={despeckle}
          setDespeckle={setDespeckle}
          simplifyCurves={simplifyCurves}
          setSimplifyCurves={setSimplifyCurves}
          imageDimensions={imageDimensions}
          ai={ai}
          setAi={setAi}
          colors={colors}
          setColors={setColors}
          setSvgPreview={setSvgPreview}
          setPreviewReady={setPreviewReady}
          setSvg={setSvg}
          setShareLink={setShareLink}
          setIsModalVisible={setIsModalVisible}
          setDownloadSVGLink={setDownloadSVGLink}
          setIsLoading={setIsLoading}
          setLoadingConverter={props.setLoadingConverter}
          setConverterOpen={props.setConverterOpen}
          clearFields={clearFields}
          saveToCloud={saveToCloud}
          wasUpscaled={wasUpscaled}
          setWasUpscaled={setWasUpscaled}
          upscaleReason={upscaleReason}
          svgPreview={svgPreview}
          svgRaw={svg}
          guestMode={isGuestMode}
          pendingFileId={pendingFileId}
          onPaywallTrigger={handlePaywallTrigger}
          aiAnalysisResult={effectiveAiAnalysisResult}
          useMorphology={useMorphology}
          setUseMorphology={setUseMorphology}
        />
      )}
      {file !== "" && props.converterOpen && window.innerWidth > 750 && (
        <ConverterDesktop
          onRetracedArtwork={handleRetracedArtwork}
          scale={scale}
          setScale={setScale}
          file={file}
          setFile={setFile}
          clearFields={clearFields}
          crop={crop}
          setCrop={setCrop}
          setIsCropVisible={setIsCropVisible}
          screenSize={screenSize}
          setClickedColor={setClickedColor}
          imageDimensions={imageDimensions}
          setImageDimensions={setImageDimensions}
          originalDimensions={originalDimensions}
          setOriginalDimensions={setOriginalDimensions}
          setCropGlobal={setCrop}
          largeImageWarning={largeImageWarning}
          isCropVisible={isCropVisible}
          downloadSVGLink={downloadSVGLink}
          setDownloadSVGLink={setDownloadSVGLink}
          isLoading={isLoading}
          setIsLoading={setIsLoading}
          smoothness={smoothness}
          setSmoothness={setSmoothness}
          despeckle={despeckle}
          setDespeckle={setDespeckle}
          simplifyCurves={simplifyCurves}
          setSimplifyCurves={setSimplifyCurves}
          ai={false}
          setAi={setAi}
          colors={colors}
          setColors={setColors}
          clickedColor={clickedColor}
          setSvgPreview={setSvgPreview}
          setPreviewReady={setPreviewReady}
          setSvg={setSvg}
          setShareLink={setShareLink}
          svgPreview={svgPreview}
          svgRaw={svg}
          saveToCloud={saveToCloud}
          setLoadingConverter={props.setLoadingConverter}
          setConverterOpen={props.setConverterOpen}
          setLargeImageWarning={setLargeImageWarning}
          wasUpscaled={wasUpscaled}
          setWasUpscaled={setWasUpscaled}
          upscaleReason={upscaleReason}
          guestMode={isGuestMode}
          pendingFileId={pendingFileId}
          onPaywallTrigger={handlePaywallTrigger}
          aiAnalysisResult={effectiveAiAnalysisResult}
          useMorphology={useMorphology}
          setUseMorphology={setUseMorphology}
        />
      )}

      {/* Share Your Creation Modal */}
      {/* The Modal component was removed, so this block is now empty */}
    </>
  );
};

export default Converter;
